
import React, { createContext, useReducer, useContext, useEffect, useMemo, useCallback } from 'react';

const SearchContext = createContext();

const initialState = {
  products: [],
  filters: [],
  activeFilters: {},
  sortOption: 'Relevance',
  loading: false,
  initialLoadComplete: false,
  totalResults: 0,
  currentPage: 1,
  isLastPage: false,
  productsPerPage: 24,
  searchQuery: '',
  closestMatchProducts: []
};

const actionTypes = {
  SET_PRODUCTS: 'SET_PRODUCTS',
  APPEND_PRODUCTS: 'APPEND_PRODUCTS',
  SET_FILTERS: 'SET_FILTERS',
  SET_ACTIVE_FILTERS: 'SET_ACTIVE_FILTERS',
  SET_SORT_OPTION: 'SET_SORT_OPTION',
  SET_LOADING: 'SET_LOADING',
  SET_INITIAL_LOAD_COMPLETE: 'SET_INITIAL_LOAD_COMPLETE',
  SET_TOTAL_RESULTS: 'SET_TOTAL_RESULTS',
  SET_CURRENT_PAGE: 'SET_CURRENT_PAGE',
  SET_IS_LAST_PAGE: 'SET_IS_LAST_PAGE',
  SET_PRODUCTS_PER_PAGE: 'SET_PRODUCTS_PER_PAGE',
  SET_SEARCH_QUERY: 'SET_SEARCH_QUERY',
  SET_CLOSEST_MATCH_PRODUCTS: 'SET_CLOSEST_MATCH_PRODUCTS'
};

function searchReducer(state, action) {
  switch (action.type) {
    case actionTypes.SET_PRODUCTS:
      return { ...state, products: action.payload };
    case actionTypes.APPEND_PRODUCTS:
      return { ...state, products: [...state.products, ...action.payload] };
    case actionTypes.SET_FILTERS:
      return { ...state, filters: action.payload };
    case actionTypes.SET_ACTIVE_FILTERS:
      return { ...state, activeFilters: action.payload };
    case actionTypes.SET_SORT_OPTION:
      return { ...state, sortOption: action.payload };
    case actionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
    case actionTypes.SET_INITIAL_LOAD_COMPLETE:
      return { ...state, initialLoadComplete: action.payload };
    case actionTypes.SET_TOTAL_RESULTS:
      return { ...state, totalResults: action.payload };
    case actionTypes.SET_CURRENT_PAGE:
      return { ...state, currentPage: action.payload };
    case actionTypes.SET_IS_LAST_PAGE:
      return { ...state, isLastPage: action.payload };
    case actionTypes.SET_PRODUCTS_PER_PAGE:
      return { ...state, productsPerPage: action.payload };
    case actionTypes.SET_SEARCH_QUERY:
      return { ...state, searchQuery: action.payload };
    case actionTypes.SET_CLOSEST_MATCH_PRODUCTS:
      return { ...state, closestMatchProducts: action.payload };
    default:
      return state;
  }
}

export const SearchProvider = ({ children, defaultSettings = {} }) => {
  const [state, dispatch] = useReducer(searchReducer, {
    ...initialState,
    productsPerPage: defaultSettings.productsPerPage || initialState.productsPerPage,
    sortOption: defaultSettings.defaultSortOption || initialState.sortOption,
  });

  useEffect(() => {
    // Handle search results
    const handleSearchComplete = (event) => {
      const { products, closestMatchProducts, totalResults, query, page, hasNextPage } = event.detail;

      if (event.detail.type == 'search') {
        // console.log('SearchContext: Search results received', products, totalResults, query, page);
        
        // Check if products are the same as current products to avoid unnecessary updates
        const incomingProducts = products || [];
        const currentProducts = state.products || [];
        
        // Helper function to compare products by their IDs
        const areProductsSame = (products1, products2) => {
          if (products1.length !== products2.length) return false;
          
          const ids1 = products1.map(p => p.id).sort();
          const ids2 = products2.map(p => p.id).sort();
          
          return ids1.every((id, index) => id === ids2[index]);
        };

        // Determine how to handle products based on pagination type and context
        const isPageBasedPagination = event.detail.paginationType === 'pages';
        const isLoadMorePagination = event.detail.paginationType === 'load_more';
        const isSubsequentPage = page > 1;
        const isFiltering = event.detail.isFiltering;

        // For load_more pagination, always append products (even on page 1 after initial load)
        if (isLoadMorePagination && !isFiltering) {
          // Check if this is truly a load more request or just a page reload
          const isInitialLoad = page === 1 && currentProducts.length === 0;
          
          if (isInitialLoad) {
            // First load, replace products
            dispatch({ type: actionTypes.SET_PRODUCTS, payload: products });

            if (!state.initialLoadComplete) {
              dispatch({ type: actionTypes.SET_CLOSEST_MATCH_PRODUCTS, payload: closestMatchProducts || [] });
            }
          } else {
            // Load more or sequential load - append new products
            const currentProductIds = new Set(currentProducts.map(p => p.id));
            const newProducts = incomingProducts.filter(p => !currentProductIds.has(p.id));
            
            if (newProducts.length > 0) {
              dispatch({ type: actionTypes.APPEND_PRODUCTS, payload: newProducts });
            } else {}
          }
        } else {
          // For page-based or filtering, check if products OR totalResults are different
          const totalResultsChanged = totalResults !== state.totalResults;
          const productsChanged = !areProductsSame(incomingProducts, currentProducts);
          
          // Special case: if totalResults is 0, always update products to empty array
          const shouldForceEmptyUpdate = totalResults === 0 && currentProducts.length > 0;
          
          // ALWAYS update products if totalResults is 0, regardless of other conditions
          if (totalResults === 0) {
            dispatch({ type: actionTypes.SET_PRODUCTS, payload: [] });
          } else if (productsChanged || totalResultsChanged || shouldForceEmptyUpdate) {
            dispatch({ type: actionTypes.SET_PRODUCTS, payload: products });
          }

          if (!state.initialLoadComplete) {
            dispatch({ type: actionTypes.SET_CLOSEST_MATCH_PRODUCTS, payload: closestMatchProducts || [] });
          }
        }
        
        dispatch({ type: actionTypes.SET_TOTAL_RESULTS, payload: totalResults });
        dispatch({ type: actionTypes.SET_SEARCH_QUERY, payload: query });
        if (typeof page === 'number') {
          dispatch({ type: actionTypes.SET_CURRENT_PAGE, payload: page });
        }
        
        // Use hasNextPage from GraphQL response if available, otherwise fallback to calculation
        let isLastPage;
        if (typeof hasNextPage === 'boolean') {
          isLastPage = !hasNextPage;
        } else {
          // Fallback calculation for backward compatibility
          const productsPerPage = state.productsPerPage || 24;
          isLastPage = (page || 1) * productsPerPage >= (totalResults || 0);
        }
        dispatch({ type: actionTypes.SET_IS_LAST_PAGE, payload: isLastPage });
      }
      dispatch({ type: actionTypes.SET_LOADING, payload: false });
      dispatch({ type: actionTypes.SET_INITIAL_LOAD_COMPLETE, payload: true });

      // Dispatch custom event to trigger scroll restoration after search results are loaded
      window.dispatchEvent(new CustomEvent('search-products-loaded', {
        detail: {
          type: 'search',
          totalResults: totalResults,
          page: page,
          query: query
        }
      }));
    };
    // Handle filter results
    const handleFiltersResults = (event) => {
      const { filters } = event.detail;
      
      if (event.detail.type == 'search'){
        dispatch({ type: actionTypes.SET_FILTERS, payload: filters });
      }
    };
    // Handle filter applied
    const handleFilterApplied = (event) => {
      const { filters_object } = event.detail;
      if (event.detail.type == 'search') {
        dispatch({ type: actionTypes.SET_CURRENT_PAGE, payload: 1 });
        dispatch({ type: actionTypes.SET_ACTIVE_FILTERS, payload: filters_object });
        dispatch({ type: actionTypes.SET_LOADING, payload: true });
      }
    };
    window.addEventListener('searchrequest-products-complete', handleSearchComplete);
    window.addEventListener('filter-list', handleFiltersResults);
    window.addEventListener('searchrequest-filter-applied', handleFilterApplied);
    return () => {
      window.removeEventListener('searchrequest-products-complete', handleSearchComplete);
      window.removeEventListener('filter-list', handleFiltersResults);
      window.removeEventListener('searchrequest-filter-applied', handleFilterApplied);
    };
  }, [state.productsPerPage]);
  const applyFilters = useCallback((filterObject, contextType = 'search', triggerSearch = true ) => {
    dispatch({ type: actionTypes.SET_CURRENT_PAGE, payload: 1 });
    dispatch({ type: actionTypes.SET_ACTIVE_FILTERS, payload: filterObject });
    dispatch({ type: actionTypes.SET_LOADING, payload: true });
    const url = new URL(window.location.href);
    url.searchParams.set('page', '1');
    window.history.replaceState({}, '', url);
    const filterEvent = new CustomEvent('searchrequest-filter-applied', {
      detail: {
        filters_object: filterObject,
        type: contextType,
        sortOption: state.sortOption,
        triggerSearch
      },
    });
    window.dispatchEvent(filterEvent);
  }, [state.sortOption]);

  const changeSortOption = useCallback((option, contextType = 'search') => {
    dispatch({ type: actionTypes.SET_SORT_OPTION, payload: option });
    dispatch({ type: actionTypes.SET_LOADING, payload: true });
    const sortEvent = new CustomEvent('searchrequest-sortchanged', {
      detail: {
        sortOption: option,
        type: contextType,
        filters_object: state.activeFilters
      },
    });
    window.dispatchEvent(sortEvent);
  }, [state.activeFilters]);

  const changePage = useCallback((page, contextType = 'search') => {
    dispatch({ type: actionTypes.SET_CURRENT_PAGE, payload: page });
    dispatch({ type: actionTypes.SET_LOADING, payload: true });
    const pageEvent = new CustomEvent('searchrequest-nextpage', {
      detail: {
        page,
        type: contextType,
        filters_object: state.activeFilters,
        sortOption: state.sortOption
      },
    });
    window.dispatchEvent(pageEvent);
  }, [state.activeFilters, state.sortOption]);

  // Load more products for load_more pagination type
  const loadMoreProducts = useCallback((contextType = 'search') => {
    if (state.loading || state.isLastPage) return;

    const nextPage = state.currentPage + 1;
    dispatch({ type: actionTypes.SET_CURRENT_PAGE, payload: nextPage });
    dispatch({ type: actionTypes.SET_LOADING, payload: true });

    // Update URL to reflect new page
    const url = new URL(window.location.href);
    url.searchParams.set('page', nextPage.toString());
    window.history.replaceState({}, '', url);

    // Dispatch searchrequest event for load more with paginationType
    const eventDetail = {
      type: contextType,
      query: state.searchQuery,
      filters_object: state.activeFilters,
      productsPerPage: state.productsPerPage,
      page: nextPage,
      paginationType: 'load_more',
      sortOption: state.sortOption,
      skipUrlUpdate: true // We already updated the URL above
    };
    window.dispatchEvent(
      new CustomEvent('searchrequest', {
        detail: eventDetail
      })
    );
  }, [state.loading, state.isLastPage, state.currentPage, state.searchQuery, state.activeFilters, state.productsPerPage, state.sortOption]);

  const contextValue = useMemo(() => ({
    ...state,
    dispatch,
    actionTypes,
    changeSortOption,
    applyFilters,
    changePage,
    loadMoreProducts
  }), [state, changeSortOption, applyFilters, changePage, loadMoreProducts]);

  return (
    <SearchContext.Provider value={contextValue}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearchContext = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearchContext must be used within a SearchProvider');
  }
  return context;
};

export default SearchContext;
